/**
 * The Review Cycle contract — one flat router, `reviewCycleContract` (`/review-cycles`). See
 * `docs/plans/performance-management/performance-reviews-plan.md`'s "Contract & API" section —
 * `tsRestRouter` rejects a nested sub-router, so this, `competencyContract`, and `reviewContract`
 * are three independent top-level routers, never one nesting another.
 *
 * WHERE CLAUDE.md INVARIANT 4 LANDS ON THIS SECTION: `legalEntityId` is nullable (org-wide default,
 * per-entity override — mirroring `WorkflowDefinition`'s own `legalEntityId`), and the cycle's
 * fiscal-year arithmetic is never a stored integer — it's derived from the person's Employment ->
 * LegalEntity -> `fiscalYearStartMonth` via `apps/api/src/lib/fiscal.ts` at read time. That's why
 * `reviewCycleSchema` below has no `fiscalYear` field at all.
 *
 * `assignReviews` IS 202 + WORKER, NOT A SYNCHRONOUS TRANSACTION. A population filter over a few
 * thousand employees creating one `PerformanceReview` row each cannot run inside one request/
 * transaction without risking a timeout — see the plan's T9. The response schema reflects that: a
 * request returns `{created, skipped}` as the WORKER's eventual count (the route's own contract
 * doesn't distinguish sync/async completion in its shape; the timing contract is `apps/api`'s to
 * keep, see routes/review-cycle.ts once T9 lands).
 *
 * `ratingBands` FOLLOWS `WorkflowDefinition.steps`'S PRECEDENT — a typed Json column, not a side
 * table, so HR can retune "Exceeds Expectations" without a migration every time.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { REVIEW_CYCLE_STATUSES, REVIEW_CYCLE_TYPES } from '../enums.js';
import {
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  percentSchema,
  ratingValueSchema,
  uuidSchema,
} from '../schemas/common.js';
import { calibrationScopeSchema, calibrationSessionStatusSchema } from './calibration.contract.js';
import {
  performanceReviewSummarySchema,
  reviewStageSchema,
  reviewStatusSchema,
} from './review.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared enum schemas
// ---------------------------------------------------------------------------

export const reviewCycleTypeSchema = z.enum(REVIEW_CYCLE_TYPES);
export const reviewCycleStatusSchema = z.enum(REVIEW_CYCLE_STATUSES);

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/** One entry of `ReviewCycle.ratingBands` — targets a calibration COHORT distribution (e.g. "no
 * more than 20% ± 5 at Outstanding"), never a score->label mapping — it has no `minScore` to sort
 * or compare a score against. See `scoreBandSchema` below (PRF-11) for that. */
export const ratingBandSchema = z.object({
  band: z.string(),
  targetPercent: percentSchema,
  tolerance: percentSchema,
});
/** Named distinctly from `@hrms/shared/performance`'s `RatingBand` (score.ts) to avoid an export
 * collision at the package root — same shape, different module boundary. */
export type CycleRatingBand = z.infer<typeof ratingBandSchema>;

/** One entry of `ReviewCycle.scoreBands` (PRF-11) — a score->label mapping, structurally
 * identical to `@hrms/shared/performance`'s `RatingBand`, which every consumer reads this through
 * via `ratingBandFor` (never a second, hand-rolled threshold set — PRF-31). `minScore` must fall
 * within `[1, ratingScaleMax]`; the route sorts and validates on save. */
export const scoreBandSchema = z.object({
  band: z.string().trim().min(1).max(60),
  minScore: decimalStringSchema,
});
export type ScoreBand = z.infer<typeof scoreBandSchema>;

/** `targetScoreByGrade` is a sibling concept to `ratingBands` above, not a duplicate of it:
 * `ratingBands` targets a COHORT distribution ("20% of reviews land at Outstanding"), while
 * `targetScoreByGrade` targets an INDIVIDUAL's overall score against their grade's expectation.
 * Follows `Competency.expectedLevelByGrade`'s exact shape — keyed by GradeCatalogue.id (PRF-32;
 * migrated off the earlier free-text grade code), values are decimal-string scores read against
 * this cycle's own `ratingScaleMax`. */

export const reviewCycleSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  legalEntityId: uuidSchema.nullable(),
  name: z.string(),
  /** The badge text, e.g. "FY 2024-25 Q3". */
  label: z.string(),
  cycleType: reviewCycleTypeSchema,
  startsOn: dateOnlySchema,
  endsOn: dateOnlySchema,
  selfAssessmentDueOn: dateOnlySchema.nullable(),
  peerFeedbackDueOn: dateOnlySchema.nullable(),
  managerReviewDueOn: dateOnlySchema.nullable(),
  status: reviewCycleStatusSchema,
  templateId: uuidSchema.nullable(),
  /** A rating written under a 5-point cycle is never reinterpreted against a later 10-point one —
   * see the plan's Data model section. */
  ratingScaleMax: z.number().int().min(2).max(10),
  ratingBands: z.array(ratingBandSchema),
  scoreBands: z.array(scoreBandSchema),
  targetScoreByGrade: z.record(z.string(), decimalStringSchema),
  /** Decimal strings, invariant 2 — should sum to 100 (the route enforces this, 422 on save). */
  goalWeightPercent: percentSchema,
  competencyWeightPercent: percentSchema,
  minPeerResponses: z.number().int().min(0),
  /**
   * Opt-in, off by default (null). Drives the `merit_cycle_budget` workflow trigger:
   * `closeCycle` fires `review.cycle.merit_check.requested`, and `apps/worker`'s
   * `merit-cycle-trigger.ts` no-ops when this is null rather than defaulting to a hardcoded
   * bar — see that worker's header. Shape-bound the same way `finalRating` is
   * (`ratingValueSchema` — `Decimal(3,1)`, 0-10); the route additionally enforces
   * `0 < x <= ratingScaleMax` against THIS cycle's own scale, the same convention
   * `ratingValueSchema`'s own comment describes for `finalRating`.
   */
  highPerformerThreshold: ratingValueSchema.nullable(),
  /** PRF-13 — opt-in per cycle, off by default. Each has its OWN deadline, independent of
   * `peerFeedbackDueOn` above. */
  upwardReviewEnabled: z.boolean(),
  skipLevelReviewEnabled: z.boolean(),
  upwardFeedbackDueOn: dateOnlySchema.nullable(),
  skipLevelFeedbackDueOn: dateOnlySchema.nullable(),
  /** The peer-nomination flow's bounds — how many peers an employee must (min) and may (max)
   * nominate. Applies only to `peerNominationContract`'s `submitNomination`, never to the
   * pre-existing freeform `feedback.createRequests` self-service path. */
  peerNominationMinCount: z.number().int().min(0),
  peerNominationMaxCount: z.number().int().min(0),
  /** When `releaseResults` made this cycle's results visible to employees; null until then.
   * Optional on the wire until every serializer sets it. */
  resultsReleasedAt: instantSchema.nullable().optional(),
  createdAt: instantSchema,
});
export type ReviewCycle = z.infer<typeof reviewCycleSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listCyclesQuerySchema = paginationQuerySchema.extend({
  legalEntityId: uuidSchema.optional(),
  status: reviewCycleStatusSchema.optional(),
});

/**
 * `endsOn >= startsOn`, refined below — matches `createGoalBodySchema`'s `dueDate >= startDate`
 * shape. `legalEntityId` omitted means the org-wide default (see this file's header).
 */
export const createCycleBodySchema = z
  .object({
    legalEntityId: uuidSchema.optional(),
    name: z.string().trim().min(1).max(200),
    label: z.string().trim().min(1).max(100),
    cycleType: reviewCycleTypeSchema,
    startsOn: dateOnlySchema,
    endsOn: dateOnlySchema,
    selfAssessmentDueOn: dateOnlySchema.optional(),
    peerFeedbackDueOn: dateOnlySchema.optional(),
    managerReviewDueOn: dateOnlySchema.optional(),
    templateId: uuidSchema.optional(),
    ratingScaleMax: z.number().int().min(2).max(10).optional(),
    ratingBands: z.array(ratingBandSchema).max(20).optional(),
    scoreBands: z.array(scoreBandSchema).max(10).optional(),
    targetScoreByGrade: z.record(z.string(), decimalStringSchema).optional(),
    goalWeightPercent: percentSchema.optional(),
    competencyWeightPercent: percentSchema.optional(),
    minPeerResponses: z.number().int().min(0).max(50).optional(),
    highPerformerThreshold: ratingValueSchema.optional(),
    upwardReviewEnabled: z.boolean().optional(),
    skipLevelReviewEnabled: z.boolean().optional(),
    upwardFeedbackDueOn: dateOnlySchema.optional(),
    skipLevelFeedbackDueOn: dateOnlySchema.optional(),
    peerNominationMinCount: z.number().int().min(0).max(50).optional(),
    peerNominationMaxCount: z.number().int().min(0).max(50).optional(),
  })
  .refine((body) => body.endsOn >= body.startsOn, {
    message: 'endsOn must be on or after startsOn',
    path: ['endsOn'],
  })
  .refine(
    (body) =>
      body.peerNominationMinCount === undefined ||
      body.peerNominationMaxCount === undefined ||
      body.peerNominationMinCount <= body.peerNominationMaxCount,
    { message: 'peerNominationMinCount must be at most peerNominationMaxCount', path: ['peerNominationMinCount'] },
  );

/** Every field optional — a partial update. The route re-validates weight/date pairing against
 * whichever value is unchanged, same convention as `updateGoalBodySchema`.
 *
 * LOCKED ONCE ACTIVE (performance remediation §5B row 4, #17, T2.12): a `draft` cycle accepts every
 * field. An `active` cycle accepts only `name`, `label` and the five due dates
 * (`selfAssessmentDueOn`, `peerFeedbackDueOn`, `managerReviewDueOn`, `upwardFeedbackDueOn`,
 * `skipLevelFeedbackDueOn`) moved LATER (a first value for an unset due date counts as later).
 * Everything else — the scoring configuration (`templateId`, `ratingScaleMax`, `ratingBands`,
 * `scoreBands`, `targetScoreByGrade`, the two weight percents, `highPerformerThreshold`), the cycle's
 * shape (`startsOn`/`endsOn`) and the 360° settings (`minPeerResponses`, `upwardReviewEnabled`,
 * `skipLevelReviewEnabled`, the peer-nomination bounds) — is refused with a 409 whose
 * `details.lockedFields` names each one. A field resent with its CURRENT value is not a change and
 * is accepted, so a client may send the whole form. From `in_calibration` on nothing is editable
 * (409). `cycleType` and `legalEntityId` are not in this body at all — they are fixed at create. */
export const updateCycleBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  label: z.string().trim().min(1).max(100).optional(),
  startsOn: dateOnlySchema.optional(),
  endsOn: dateOnlySchema.optional(),
  selfAssessmentDueOn: dateOnlySchema.optional(),
  peerFeedbackDueOn: dateOnlySchema.optional(),
  managerReviewDueOn: dateOnlySchema.optional(),
  templateId: uuidSchema.optional(),
  ratingScaleMax: z.number().int().min(2).max(10).optional(),
  ratingBands: z.array(ratingBandSchema).max(20).optional(),
  scoreBands: z.array(scoreBandSchema).max(10).optional(),
  targetScoreByGrade: z.record(z.string(), decimalStringSchema).optional(),
  goalWeightPercent: percentSchema.optional(),
  competencyWeightPercent: percentSchema.optional(),
  minPeerResponses: z.number().int().min(0).max(50).optional(),
  highPerformerThreshold: ratingValueSchema.optional(),
  upwardReviewEnabled: z.boolean().optional(),
  skipLevelReviewEnabled: z.boolean().optional(),
  upwardFeedbackDueOn: dateOnlySchema.optional(),
  skipLevelFeedbackDueOn: dateOnlySchema.optional(),
  /** Re-validated against whichever of this pair is unchanged, same convention as
   * `highPerformerThreshold`/`scoreBands` above — the route checks min<=max using the cycle's
   * currently-effective values for any field this call didn't resend. */
  peerNominationMinCount: z.number().int().min(0).max(50).optional(),
  peerNominationMaxCount: z.number().int().min(0).max(50).optional(),
});
export type UpdateCycleBody = z.infer<typeof updateCycleBodySchema>;

export const listCycleReviewsQuerySchema = paginationQuerySchema.extend({
  status: z.string().optional(),
});

/**
 * A population filter, not a list of person ids — `assignReviews` is meant to run against "every
 * confirmed employee in Engineering", not a hand-picked roster. Any combination of filters narrows
 * the population (AND, not OR); omitting all three targets the whole org.
 */
export const assignReviewsBodySchema = z.object({
  legalEntityId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
});

export const assignReviewsResponseSchema = z.object({
  created: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});

/** Cap on `pendingReviews` in a refused release — the count is always exact. */
export const RELEASE_BLOCKERS_LIST_LIMIT = 100;

/**
 * Why `releaseResults` refused (the 409's `details`). Release requires every calibration session of
 * the cycle to be `finalized` (none created is fine) and every non-cancelled review to have reached
 * `manager_reviewed`; stragglers must be completed or cancelled (`reviews.cancelReview`) first.
 */
export const releaseResultsBlockersSchema = z.object({
  unfinalizedSessions: z.array(
    z.object({
      id: uuidSchema,
      scope: calibrationScopeSchema,
      scopeId: uuidSchema.nullable(),
      status: calibrationSessionStatusSchema,
    }),
  ),
  /** Exact number of non-cancelled reviews not yet `manager_reviewed`. */
  pendingReviewCount: z.number().int().nonnegative(),
  /** The first `RELEASE_BLOCKERS_LIST_LIMIT` of them. */
  pendingReviews: z.array(
    z.object({
      id: uuidSchema,
      personId: uuidSchema,
      personName: z.string(),
      stage: reviewStageSchema,
      status: reviewStatusSchema,
    }),
  ),
});
export type ReleaseResultsBlockers = z.infer<typeof releaseResultsBlockersSchema>;

/** A refused release: the standard error body, with `details` carrying the blocking items when the
 * refusal is about them (a wrong cycle status is a plain 409 without `details`). */
export const releaseResultsConflictSchema = errorBodySchema.extend({
  details: releaseResultsBlockersSchema.optional(),
});

/**
 * Why `closeCycle` refused a released cycle (the 409's `details`): non-cancelled reviews that are
 * neither released nor acknowledged — an open appeal (`appealed`) or a review reopened by an
 * approved appeal and not yet re-released. A wrong cycle status is a plain 409 without `details`.
 */
export const closeCycleConflictSchema = errorBodySchema.extend({
  details: z
    .object({
      blockingReviewCount: z.number().int().nonnegative(),
      /** The first `RELEASE_BLOCKERS_LIST_LIMIT` of them. */
      blockingReviews: z.array(
        z.object({
          id: uuidSchema,
          personId: uuidSchema,
          personName: z.string(),
          stage: reviewStageSchema,
          status: reviewStatusSchema,
        }),
      ),
    })
    .optional(),
});

// ---------------------------------------------------------------------------
// Contract — /review-cycles
// ---------------------------------------------------------------------------

export const reviewCycleContract = c.router(
  {
    listCycles: {
      method: 'GET',
      path: '/',
      query: listCyclesQuerySchema,
      responses: {
        200: paginatedSchema(reviewCycleSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'List review cycles',
      metadata: routeMeta('review:read'),
    },

    getCycle: {
      method: 'GET',
      path: '/:cycleId',
      responses: {
        200: reviewCycleSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one review cycle',
      metadata: routeMeta('review:read'),
    },

    createCycle: {
      method: 'POST',
      path: '/',
      body: createCycleBodySchema,
      responses: {
        201: reviewCycleSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a review cycle — review.cycle:manage',
      metadata: routeMeta('review.cycle:manage'),
    },

    updateCycle: {
      method: 'PATCH',
      path: '/:cycleId',
      body: updateCycleBodySchema,
      responses: {
        200: reviewCycleSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** A locked field changed on a live cycle (`details.lockedFields`), a due date moved
         * earlier (`details.earlierDueDates`), or the cycle is past `active`. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Update a review cycle — review.cycle:manage. Draft: every field; active: name, label and later due dates only (409 naming the locked fields); in calibration or later: nothing (409)',
      metadata: routeMeta('review.cycle:manage'),
    },

    activateCycle: {
      method: 'POST',
      path: '/:cycleId/activate',
      body: z.object({}),
      responses: {
        200: reviewCycleSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Move a cycle from draft to active',
      metadata: routeMeta('review.cycle:manage'),
    },

    startCalibration: {
      method: 'POST',
      path: '/:cycleId/start-calibration',
      body: z.object({}),
      responses: {
        200: reviewCycleSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Move cycle from active to in_calibration',
      metadata: routeMeta('review.cycle:manage'),
    },

    closeCycle: {
      method: 'POST',
      path: '/:cycleId/close',
      body: z.object({}),
      responses: {
        200: reviewCycleSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: closeCycleConflictSchema,
      },
      summary:
        'Close a RELEASED cycle (409 from any other status, or while a review is still appealed/not released — details.blockingReviews) — every non-cancelled review becomes closed; emits review.cycle.closed (ReviewCycleRollup) and review.cycle.merit_check.requested (the merit_cycle_budget workflow trigger, no-op unless highPerformerThreshold is set)',
      metadata: routeMeta('review.cycle:manage'),
    },

    listCycleReviews: {
      method: 'GET',
      path: '/:cycleId/reviews',
      query: listCycleReviewsQuerySchema,
      responses: {
        200: paginatedSchema(performanceReviewSummarySchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "The cycle's roster — one row per PerformanceReview, for the admin roster screen",
      metadata: routeMeta(null),
    },

    assignReviews: {
      method: 'POST',
      path: '/:cycleId/assign-reviews',
      body: assignReviewsBodySchema,
      responses: {
        202: assignReviewsResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Create one PerformanceReview per person in the filtered population — 202+worker, chunked; re-running is a no-op per already-assigned person (the @@unique makes it idempotent)',
      metadata: routeMeta('review.cycle:manage'),
    },

    releaseResults: {
      method: 'POST',
      path: '/:cycleId/release',
      body: z.object({}),
      responses: {
        200: reviewCycleSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: releaseResultsConflictSchema,
      },
      summary:
        "Release the cycle's results to employees (cycle in_calibration, or active for a cycle that never opened a calibration session -> released; each manager_reviewed review -> released, finalRating defaulted to overallScore; each reviewee notified) — 409 with the blocking calibration sessions and unfinished reviews. Requires Idempotency-Key",
      metadata: routeMeta('review.cycle:manage'),
    },
  },
  { pathPrefix: '/review-cycles' },
);
